import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Modal,
  Stack,
  TextField,
  Divider,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import {useAuthFrontendApis} from '@propelauth/frontend-apis-react';
import {useTheme} from '@mui/material/styles';
import {BLACK_100, WHITE_100} from '../../theme';
import Loading from '../../components/Loading';

const modalStyles = {
  base: {
    borderRadius: '0.5rem',
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
    backgroundColor: WHITE_100,
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: '700',
    lineHeight: '120%',
    color: '#043B34',
  },
  divider: {
    borderBottomWidth: '0.0625rem',
    opacity: 1,
    color: '#DDe0DE',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#043B34',
    marginBottom: '8px',
  },
  confirmButton: {
    background: '#26A993',
    borderRadius: '0.5rem',
    paddingVertical: '0.625rem',
    height: '2.5rem',
    '&:hover': {
      backgroundColor: '#26A993',
    },
  },
  confirmButtonLabel: {
    fontSize: '0.875rem',
    lineHeight: '140%',
    fontWeight: '500',
  },
  cancelButton: {
    borderRadius: '0.5rem',
    paddingVertical: '0.6825rem',
    paddingHorizontal: '1rem',
    height: '2.5rem',
    '&:hover': {
      backgroundColor: WHITE_100,
    },
  },
  cancelButtonLabel: {
    color: '#043B34',
    fontSize: '0.875rem',
    fontWeight: '500',
    lineHeight: '130%',
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      display: 'flex',
      padding: '12px 16px',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      borderRadius: '8px',
      border: '1px solid #E1E5E4',
      height: '42px',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
      '&.Mui-focused': {
        borderColor: '#26A993',
      },
    },
    '& .MuiInputBase-input': {
      padding: '0',
    },
  },
  roleSelect: {
    '& .MuiOutlinedInput-root': {
      display: 'flex',
      padding: '12px 16px',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      borderRadius: '8px',
      border: '1px solid #E1E5E4',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
      '&.Mui-focused': {
        borderColor: '#26A993',
      },
    },
    '& .MuiSelect-select': {
      padding: '0',
    },
  },
};

const POSSIBLE_INVITEE_ROLES = ['Admin', 'Reviewer', 'Report Preparer'];

const InviteUserModal = ({
  open,
  onClose,
  orgId,
  orgName,
  onShowSnackbar,
  forceRole,
}) => {
  const {inviteUserToOrg} = useAuthFrontendApis();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [inviteeRole, setInviteeRole] = useState(
    forceRole || POSSIBLE_INVITEE_ROLES[0]
  );

  // Clear form when modal closes
  useEffect(() => {
    if (!open) {
      setInviteeEmail('');
      setInviteeRole(forceRole || POSSIBLE_INVITEE_ROLES[0]);
    }
  }, [open, forceRole]);

  const handleInvite = async () => {
    if (!inviteeEmail.trim()) return;

    setIsLoading(true);

    try {
      const response = await inviteUserToOrg({
        org_id: orgId,
        email: inviteeEmail.trim(),
        role: forceRole || inviteeRole,
      });

      await response.handle({
        success: () => {
          onShowSnackbar({
            message: `Invitation sent to ${inviteeEmail}`,
            severity: 'success',
          });
          setInviteeEmail('');
          setInviteeRole(forceRole || POSSIBLE_INVITEE_ROLES[0]);
          onClose();
        },
        badRequest: (error) => {
          const emailError = error.user_facing_errors?.email;
          const roleError = error.user_facing_errors?.role;

          if (emailError) {
            onShowSnackbar({
              message: `Email error: ${emailError}`,
              severity: 'error',
            });
          } else if (roleError) {
            if (!forceRole) {
              onShowSnackbar({
                message: `Role error: ${roleError}`,
                severity: 'error',
              });
            } else {
              onShowSnackbar({
                message: 'Invalid invitation details',
                severity: 'error',
              });
            }
          } else {
            onShowSnackbar({
              message: 'Invalid invitation details',
              severity: 'error',
            });
          }
        },
        orgNotFound: () => {
          onShowSnackbar({
            message: "The organization this invite is for doesn't exist",
            severity: 'error',
          });
        },
        orgsNotEnabled: () => {
          onShowSnackbar({
            message: 'Organizations are not enabled in this project',
            severity: 'error',
          });
        },
        noInvitePermission: () => {
          onShowSnackbar({
            message:
              "You don't have permission to invite users to this organization",
            severity: 'error',
          });
        },
        orgMaxUsersLimitExceeded: () => {
          onShowSnackbar({
            message: 'This organization is already at the max number of users',
            severity: 'error',
          });
        },
        userAlreadyInOrg: () => {
          onShowSnackbar({
            message: 'This user is already in your organization',
            severity: 'error',
          });
        },
        unexpectedOrUnhandled: (error) => {
          onShowSnackbar({
            message: error.user_facing_error || 'Failed to send invitation',
            severity: 'error',
          });
        },
      });
    } catch (error) {
      onShowSnackbar({
        message: 'Failed to send invitation',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setInviteeEmail('');
      setInviteeRole(POSSIBLE_INVITEE_ROLES[0]);
      onClose();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && inviteeEmail.trim() && !isLoading) {
      handleInvite();
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        position="absolute"
        sx={{left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
      >
        <Box width="28rem" minWidth="28rem" maxWidth="28rem">
          <Stack direction="column" sx={modalStyles.base}>
            <Stack
              direction="row"
              paddingY="1rem"
              paddingLeft="1.5rem"
              paddingRight="1rem"
              flex={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography sx={modalStyles.title}>
                Invite user to {orgName}
              </Typography>
              <IconButton onClick={handleClose} disabled={isLoading}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider orientation="horizontal" sx={modalStyles.divider} />
            <Stack
              direction="column"
              paddingTop="1.25rem"
              paddingBottom="1.5rem"
              paddingX="1.5rem"
              spacing="1rem"
            >
              {/* Email Input */}
              <Stack direction="column" spacing={1} paddingBottom="10px">
                <Typography sx={modalStyles.label}>Email</Typography>
                <TextField
                  variant="outlined"
                  type="email"
                  value={inviteeEmail}
                  onChange={(event) => setInviteeEmail(event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="jane@acme.com"
                  disabled={isLoading}
                  autoFocus
                  sx={modalStyles.inputField}
                  inputProps={{
                    autoComplete: 'new-password',
                    'data-1p-ignore': true,
                    'data-lpignore': true,
                    'data-form-type': 'other',
                    name: 'invite-email',
                    type: 'search',
                  }}
                />
              </Stack>

              {/* Role Selector (hidden if forceRole is provided) */}
              {!forceRole && (
                <Stack direction="column" spacing={1}>
                  <Typography sx={modalStyles.label}>Role</Typography>
                  <FormControl fullWidth sx={modalStyles.roleSelect}>
                    <Select
                      value={inviteeRole}
                      onChange={(event) => setInviteeRole(event.target.value)}
                      disabled={isLoading}
                    >
                      {POSSIBLE_INVITEE_ROLES.map((role) => (
                        <MenuItem key={role} value={role}>
                          {role}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              )}
            </Stack>
            <Divider orientation="horizontal" sx={modalStyles.divider} />
            <Stack
              direction="row"
              paddingY="1rem"
              paddingLeft="1.5rem"
              paddingRight="1rem"
              flex={1}
              justifyContent="flex-end"
              alignItems="center"
              spacing="0.5rem"
            >
              <Button
                variant="outlined"
                sx={modalStyles.cancelButton}
                onClick={handleClose}
                disabled={isLoading}
              >
                <Typography style={modalStyles.cancelButtonLabel}>
                  Cancel
                </Typography>
              </Button>
              <LoadingButton
                disabled={!inviteeEmail.trim() || isLoading}
                variant="contained"
                sx={{
                  ...theme.button.primary.large,
                  '&.Mui-disabled': {
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    color: '#fff',
                    backgroundColor: '#34A290',
                  },
                }}
                onClick={handleInvite}
                loading={isLoading}
                loadingIndicator={
                  <Loading
                    loading={isLoading}
                    loadingProps={{color: BLACK_100}}
                  />
                }
              >
                Invite
              </LoadingButton>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

InviteUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orgId: PropTypes.string.isRequired,
  orgName: PropTypes.string.isRequired,
  onShowSnackbar: PropTypes.func.isRequired,
  forceRole: PropTypes.string,
};

export default InviteUserModal;
